import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Users, CheckCircle, Home, ArrowRight } from 'lucide-react';
import { getCurrentUserData } from '../utils/auth';
import { fetchFamily } from '../services/familiesApi';

const AcceptInvitation = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [state, setState] = useState({
        loading: true,
        error: null,
        invitationData: null,
        accepting: false,
        accepted: false
    });
    
    const familyId = searchParams.get('family_id');
    const email = searchParams.get('email');
    
    const fetchInvitationDetails = useCallback(async () => {
        try {
            // Fetch family details
            const family = await fetchFamily(familyId);
            
            setState(prev => ({
                ...prev,
                loading: false,
                invitationData: family
            }));
            
            // Check if user is already a member
            const currentUser = getCurrentUserData();
            const isMember = family.members.some(m => 
                m.email.toLowerCase() === currentUser.email.toLowerCase() && 
                m.status === 'active'
            );
            
            if (isMember) {
                // Already a member - just redirect to dashboard
                localStorage.setItem('selectedFamilyId', familyId);
                navigate('/dashboard', { replace: true });
            }
            
        } catch (error) {
            console.error('[AcceptInvitation] Error fetching invitation:', error);
            setState(prev => ({
                ...prev,
                loading: false,
                error: error.message || 'Failed to load invitation details. The invitation may have expired or been revoked.'
            }));
        }
    }, [familyId, navigate]);
    
    useEffect(() => {
        // Check if user is logged in
        const currentUser = getCurrentUserData();
        
        if (!currentUser) {
            // User not logged in - redirect to login with return URL
            const returnUrl = `/accept-invitation?family_id=${familyId}&email=${email}`;
            navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
            return;
        }
        
        // Check if logged-in user matches the invitation email
        if (currentUser.email.toLowerCase() !== email.toLowerCase()) {
            setState(prev => ({
                ...prev,
                loading: false,
                error: `This invitation is for ${email}. Please log out and sign in with the correct account.`
            }));
            return;
        }
        
        // Fetch invitation and family details
        fetchInvitationDetails();
    }, [familyId, email, navigate, fetchInvitationDetails]);
    
    const handleAcceptInvitation = async () => {
        setState(prev => ({ ...prev, accepting: true }));
        
        try {
            // The invitation should already be automatically accepted on login
            // via the backend's process_pending_invitations function
            // But we'll trigger a refresh to ensure the latest data
            
            // Just verify that the user now has access to the family
            const family = await fetchFamily(familyId);
            
            const currentUser = getCurrentUserData();
            const isMember = family.members.some(m => 
                m.email.toLowerCase() === currentUser.email.toLowerCase() && 
                m.status === 'active'
            );
            
            if (isMember) {
                setState(prev => ({ ...prev, accepting: false, accepted: true }));
                
                // Store selected family and redirect to dashboard
                localStorage.setItem('selectedFamilyId', familyId);
                
                // Redirect after a brief delay to show success message
                setTimeout(() => {
                    navigate('/dashboard', { replace: true });
                }, 1500);
            } else {
                setState(prev => ({
                    ...prev,
                    accepting: false,
                    error: 'Unable to accept invitation. Please try logging out and back in.'
                }));
            }
        } catch (error) {
            console.error('[AcceptInvitation] Error accepting invitation:', error);
            setState(prev => ({
                ...prev,
                accepting: false,
                error: error.message || 'Failed to accept invitation. Please try again.'
            }));
        }
    };
    
    if (state.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                <div className="text-center">
                    <div className="mb-4">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading invitation...</h2>
                    <p className="text-gray-600">Please wait</p>
                </div>
            </div>
        );
    }
    
    if (state.error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
                    <div className="mb-6">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                            <Shield className="w-8 h-8 text-red-600" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Invitation Error</h2>
                    <p className="text-gray-600 mb-8">{state.error}</p>
                    <button
                        onClick={() => navigate('/famlis')}
                        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                        <Home className="w-5 h-5" />
                        Go to Families
                    </button>
                </div>
            </div>
        );
    }
    
    if (state.accepted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
                    <div className="mb-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to {state.invitationData?.name}!</h2>
                    <p className="text-gray-600 mb-4">You've successfully joined the family.</p>
                    <p className="text-sm text-gray-500 mb-8">Redirecting to dashboard...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-12">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="mb-4">
                        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                            <Users className="w-10 h-10 text-blue-600" />
                        </div>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">
                        You're Invited!
                    </h1>
                    <p className="text-gray-600">
                        Join your family on mifamli
                    </p>
                </div>
                
                {/* Family Details */}
                {state.invitationData && (
                    <div className="mb-8">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                            <div className="flex items-center gap-4 mb-4">
                                {state.invitationData.photo ? (
                                    <img
                                        src={state.invitationData.photo}
                                        alt={state.invitationData.name}
                                        className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-md"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-2xl font-bold border-4 border-white shadow-md">
                                        {state.invitationData.name.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1">
                                    <h2 className="text-2xl font-bold text-gray-800">
                                        {state.invitationData.name}
                                    </h2>
                                    {state.invitationData.tagline && (
                                        <p className="text-gray-600 italic">{state.invitationData.tagline}</p>
                                    )}
                                </div>
                            </div>
                            
                            {state.invitationData.description && (
                                <p className="text-gray-700 mb-4">{state.invitationData.description}</p>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Users className="w-4 h-4" />
                                <span>{state.invitationData.members?.length || 0} member{state.invitationData.members?.length !== 1 ? 's' : ''}</span>
                            </div>
                        </div>
                        
                        {/* Features */}
                        <div className="mb-6">
                            <h3 className="font-semibold text-gray-800 mb-3">What you can do:</h3>
                            <ul className="space-y-2 text-gray-600">
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Create and join family calendar events</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Add and assign to-dos and chores</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Share documents, passwords, and important info securely</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>Organize recipes, trips, and family memories</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                    <button
                        onClick={handleAcceptInvitation}
                        disabled={state.accepting}
                        className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                        {state.accepting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                <span>Accepting...</span>
                            </>
                        ) : (
                            <>
                                <span>Accept Invitation</span>
                                <ArrowRight className="w-5 h-5" />
                            </>
                        )}
                    </button>
                    
                    <button
                        onClick={() => navigate('/famlis')}
                        className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                    >
                        Maybe Later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;

